import { DataTable, EmptyState, useToast } from '@aidkitorg/component-library';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { SortingState } from '@tanstack/react-table';
import { useLocalizedStrings } from '../../../../Localization';
import { useReports, ReportData, ReportWithConfig } from '../../../../Contexts/ReportsContext';
import { useReportById } from '../../../Reports/hooks/useReportById';
import { generateReportColumns, resolveLinksToSectionInfo } from '../../../Reports/utils/reportColumns';
import { buildLinkGensByCol } from '../../../../utils/dashboardUtils';

type ReportComponentConfig = {
  kind: 'Report';
  title: string;
  description?: string;
  reportId: string;
}

type ReportProps = {
  component: ReportComponentConfig;
  height: number;
  viewFilters: any;
  dashboardFilter: any;
}

export const Report = ({ component, height, viewFilters, dashboardFilter }: ReportProps) => {
  const L = useLocalizedStrings();
  const { toast } = useToast();
  const { transformDistroReportWithConfig, generateReportDataImmediate } = useReports();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchDisplay, setSearchDisplay] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearchDisplay(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    const trimmed = value.trim();
    if (!trimmed) {
      setSearchValue('');
      setCurrentPage(1);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchValue(trimmed);
      setCurrentPage(1);
    }, 500);
  }, []);

  const onPaginationChange = useCallback((pagination: { pageIndex: number; pageSize: number }) => {
    const newPage = pagination.pageIndex + 1;
    const newPageSize = pagination.pageSize;
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    } else if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  }, [currentPage, pageSize]);

  const onSortingChange = useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    setSorting(newSorting);
    setCurrentPage(1);
  }, [sorting]);

  useEffect(() => () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  }, []);

  // Fetch report metadata, trying distro first then falling back to DB
  const { rawData, isFromDb, isError: reportFetchError } = useReportById(component.reportId);

  // Transform raw data to ReportWithConfig
  const report = useMemo(() => {
    if (!rawData) return null;
    if (isFromDb) {
      const { _fromDb, ...dbData } = rawData;
      return {
        id: dbData.id,
        title: dbData.title,
        description: dbData.description || '',
        created_by: dbData.created_by || '',
        created_by_name: dbData.created_by_name || '',
        created_at: dbData.created_at || 0,
        updated_at: dbData.updated_at || 0,
        view_only: dbData.view_only || false,
        status: dbData.status || 'published',
        config: dbData.config,
      } as ReportWithConfig;
    }
    return transformDistroReportWithConfig(rawData);
  }, [rawData, isFromDb, transformDistroReportWithConfig]);

  const customQueryComponent = useMemo(() => {
    if (!rawData || isFromDb) return null;
    return rawData?.table?.kind === 'Custom Query' ? rawData.table : null;
  }, [rawData, isFromDb]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [viewFilters, dashboardFilter]);

  // Load report data when report config, pagination, sorting, or filters change
  useEffect(() => {
    const loadReportData = async () => {
      if (!report?.config && !customQueryComponent) return;

      try {
        setIsDataLoading(true);

        const data = await generateReportDataImmediate({
          config: report?.config || { columns: [] },
          customQuery: customQueryComponent || undefined,
          page: currentPage,
          limit: pageSize,
          sorting,
          viewFilters,
          dashboardFilter,
          search: searchValue || undefined,
        });

        if (data) {
          setReportData(data);
        } else {
          toast({
            title: L.admin_panel.reports.error,
            description: L.admin_panel.reports.failed_to_load_report_data,
            variant: 'error'
          });
        }
      } catch (err) {
        console.error('Error loading report data:', err);
        toast({
          title: L.admin_panel.reports.error,
          description: L.admin_panel.reports.failed_to_load_report_data,
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
        setIsDataLoading(false);
      }
    };

    loadReportData();
  }, [report?.config, customQueryComponent, currentPage, pageSize, sorting, viewFilters, dashboardFilter, generateReportDataImmediate, toast, L, searchValue]);

  const linkGensByCol = useMemo(
    () => buildLinkGensByCol(report?.config?.columns),
    [report?.config?.columns],
  );

  const linksToSectionInfo = useMemo(
    () => resolveLinksToSectionInfo(report?.config?.linksToSection),
    [report?.config?.linksToSection],
  );

  const { columns, data } = useMemo(() => {
    if (!reportData) {
      return { columns: [], data: [] };
    }

    return {
      columns: generateReportColumns({
        reportData,
        isCustomQuery: report?._isCustomQuery,
        configColumns: report?.config?.columns,
        encryptedFields: report?.config?.encryption?.encryptedFields,
        groupByField: report?.config?.groupBy?.field,
        linksToSectionInfo,
        linkGensByCol,
      }),
      data: Array.isArray(reportData.data) ? (reportData.data as Record<string, any>[]) : [],
    };
  }, [reportData, report, linksToSectionInfo, linkGensByCol]);

  if (reportFetchError) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        <div className="text-center">
          <div className="text-sm font-medium">{L.admin_panel.dashboards.report_unavailable}</div>
        </div>
      </div>
    );
  }

  if (isLoading && !reportData) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        <div className="text-center">
          <div className="text-sm font-medium">{L.admin_panel.reports.loading_report}</div>
        </div>
      </div>
    );
  }

  return (
    <DataTable
      className="p-2 w-full"
      columns={columns}
      data={data}
      compact
      loading={isDataLoading}
      noDataMessage={<EmptyState variant="widget" />}
      showPagination={true}
      pageSize={pageSize}
      stickyHeader={true}
      tableHeight={height}
      verticalAlign="middle"
      alternateRowShading
      enableSearch
      searchLabel={L.admin_panel.search_placeholder}
      manualSearch={{
        searchValue: searchDisplay,
        onSearchChange: handleSearchChange,
        resetPageOnChange: false,
      }}
      manualPagination={{
        pageCount: reportData ? (reportData.total > 0 ? Math.ceil(reportData.total / pageSize) : 0) : 0,
        pageIndex: Math.max(0, currentPage - 1),
        totalRows: reportData?.total || 0,
        onPaginationChange,
      }}
      manualSorting={{
        sorting,
        onSortingChange: onSortingChange,
      }}
    />
  );
};
